import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/api/client';
import { notifications } from '@mantine/notifications';

// ============ Types ============

export interface FogHost {
  id: number;
  name: string;
  mac: string;
  ip?: string;
  imageID?: number;
  imageName?: string;
  description?: string;
  createdTime?: string;
  deployed?: boolean;
  pending?: boolean;
  macs?: string[];
}

export interface FogImage {
  id: number;
  name: string;
  description?: string;
  osID?: number;
  osName?: string;
  path?: string;
  size?: string;
  createdTime?: string;
  deployed?: string;
  isEnabled?: boolean;
}

export interface FogGroup {
  id: number;
  name: string;
  description?: string;
  hostcount?: number;
  createdTime?: string;
}

export interface FogTask {
  id: number;
  name?: string;
  hostID: number;
  hostName?: string;
  imageID?: number;
  imageName?: string;
  typeID: number;
  typeName?: string;
  stateID: number;
  percent?: number;
  createdTime?: string;
  checkedInTime?: string;
}

export interface FogHostsResponse {
  count?: number;
  hosts: FogHost[];
}

export interface FogImagesResponse {
  count?: number;
  images: FogImage[];
}

export interface FogGroupsResponse {
  count?: number;
  groups: FogGroup[];
}

export interface FogTasksResponse {
  count?: number;
  tasks: FogTask[];
}

// ============ Query Keys ============

export const fogQueryKeys = {
  hosts: ['fog', 'hosts'] as const,
  host: (id: number) => ['fog', 'hosts', id] as const,
  images: ['fog', 'images'] as const,
  image: (id: number) => ['fog', 'images', id] as const,
  groups: ['fog', 'groups'] as const,
  tasks: ['fog', 'tasks'] as const,
};

// ============ Hooks ============

/**
 * Fetch all FOG hosts
 */
export function useFogHosts() {
  return useQuery({
    queryKey: fogQueryKeys.hosts,
    queryFn: async () => {
      const response = await api.get<FogHostsResponse>(endpoints.fog.hosts, {
        timeout: 90000, // 90 second timeout for relay
      });
      return response;
    },
    staleTime: 30000, // Consider stale after 30 seconds
    refetchInterval: 60000, // Refetch every 60 seconds
  });
}

/**
 * Fetch single FOG host
 */
export function useFogHost(hostId: number | null) {
  return useQuery({
    queryKey: fogQueryKeys.host(hostId!),
    queryFn: async () => {
      const response = await api.get<FogHost>(endpoints.fog.host(hostId!), {
        timeout: 60000,
      });
      return response;
    },
    enabled: hostId !== null,
  });
}

/**
 * Fetch all FOG images
 */
export function useFogImages() {
  return useQuery({
    queryKey: fogQueryKeys.images,
    queryFn: async () => {
      const response = await api.get<FogImagesResponse>(endpoints.fog.images, {
        timeout: 90000,
      });
      return response;
    },
    staleTime: 60000, // Images don't change often
    refetchInterval: 120000,
  });
}

/**
 * Fetch all FOG groups
 */
export function useFogGroups() {
  return useQuery({
    queryKey: fogQueryKeys.groups,
    queryFn: async () => {
      const response = await api.get<FogGroupsResponse>(endpoints.fog.groups, {
        timeout: 90000,
      });
      return response;
    },
    staleTime: 60000,
  });
}

/**
 * Fetch active FOG tasks with auto-refresh
 */
export function useFogTasks() {
  return useQuery({
    queryKey: fogQueryKeys.tasks,
    queryFn: async () => {
      const response = await api.get<FogTasksResponse>(endpoints.fog.tasks, {
        timeout: 90000,
      });
      return response;
    },
    // Smart polling: faster when tasks are active
    refetchInterval: (query) => {
      const hasActiveTasks = query.state.data?.tasks?.some(
        (t) => t.stateID === 1 || t.stateID === 2 || t.stateID === 3
      );
      return hasActiveTasks ? 10000 : 30000; // 10s if active, 30s otherwise
    },
  });
}

/**
 * Deploy image to host
 */
export function useDeployImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hostId, imageId }: { hostId: number; imageId: number }) => {
      const response = await api.post(endpoints.fog.deploy(hostId), { imageId }, {
        timeout: 120000, // 2 minute timeout for deploy action
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate tasks to show new task
      queryClient.invalidateQueries({ queryKey: fogQueryKeys.tasks });
      queryClient.invalidateQueries({ queryKey: fogQueryKeys.hosts });
      notifications.show({
        title: 'Deploy Task Created',
        message: 'Image deployment task has been queued',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Deploy Failed',
        message: error.message || 'Failed to create deploy task',
        color: 'red',
      });
    },
  });
}

/**
 * Capture image from host
 */
export function useCaptureImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hostId, imageId }: { hostId: number; imageId: number }) => {
      const response = await api.post(endpoints.fog.capture(hostId), { imageId }, {
        timeout: 120000,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fogQueryKeys.tasks });
      queryClient.invalidateQueries({ queryKey: fogQueryKeys.hosts });
      notifications.show({
        title: 'Capture Task Created',
        message: 'Image capture task has been queued',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Capture Failed',
        message: error.message || 'Failed to create capture task',
        color: 'red',
      });
    },
  });
}

/**
 * Cancel FOG task
 */
export function useCancelFogTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const response = await api.delete(endpoints.fog.task(taskId), {
        timeout: 60000,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fogQueryKeys.tasks });
      notifications.show({
        title: 'Task Cancelled',
        message: 'FOG task has been cancelled',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Cancel Failed',
        message: error.message || 'Failed to cancel task',
        color: 'red',
      });
    },
  });
}

// ============ Helper Functions ============

export function getTaskStateLabel(stateID: number): string {
  switch (stateID) {
    case 1: return 'Queued';
    case 2: return 'Checked In';
    case 3: return 'In Progress';
    case 4: return 'Complete';
    case 5: return 'Cancelled';
    default: return 'Unknown';
  }
}

export function getTaskStateColor(stateID: number): string {
  switch (stateID) {
    case 1: return 'yellow';
    case 2: return 'blue';
    case 3: return 'cyan';
    case 4: return 'green';
    case 5: return 'red';
    default: return 'gray';
  }
}

export function getTaskTypeName(typeID: number): string {
  switch (typeID) {
    case 1: return 'Deploy';
    case 2: return 'Capture';
    case 8: return 'Multicast';
    default: return `Type ${typeID}`;
  }
}

export function isTaskActive(task: FogTask): boolean {
  return task.stateID === 1 || task.stateID === 2 || task.stateID === 3;
}
